import '../../css/popup.css';

import { dragElement } from '../drag';
import { map } from './tools';
import {
  getAreaWithUnit, getHighlightedFeature,
  highlightFeature, intersection, removeHighlight
} from './feature-tools';
import {
  getFeatureInfo, getLayerById, getSpecificFeatureInfo, getWMSes
} from './layer-tools';
import { getKat, translate as description } from '../formal';
import { getEmissions, sendFeaturesToBackend } from '../request/computation';
import {
  highlightCorrespondingListItem, removeListItemHighlight
} from '../list';
import { highlightStyle } from './styles';
import { findInHeader, sumRows, totalEmissionsFromTable } from '../table';
import { writeObject, separateObjectKeysStartingWith } from '../util';

import Overlay from 'ol/Overlay';
import WKB from 'ol/format/WKB';
import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';

const wizard = document.getElementById('wizard');
const popupElement = document.getElementById('popup');

let positionOnce = true;
const showPopup = (popup, coordinates) => {
  popup.setPosition(coordinates);
  popupElement.hidden = false;
  const h = popupElement.offsetHeight;
  const w = popupElement.offsetWidth;
  if (positionOnce) {
    popupElement.style.top = `min(100% - 40px, 100% - ${h}px - 0.5em)`;
    popupElement.style.left = `min(100% - 40px, 100% - ${w}px - 0.5em)`;
    positionOnce = false;
  } else { // increase/decrease according to delayed WMS info
    const t = popupElement.style.top;
    const l = popupElement.style.left;
    popupElement.style.top = `min(100% - 40px, 100% - ${h}px - 0.5em, ${t})`;
    popupElement.style.left = `min(100% - 40px, 100% - ${w}px - 0.5em, ${l})`;
  }
};

const getAdvancedProperties = (plan) => {
  const adv = {};
  const p = plan.getProperties();
  if ((p.plannavn || p.drawn) && p.planid) adv.planid = p.planid;
  if (p.plannavn) adv.plannavn = p.plannavn;
  // if (p.planType) adv.Plantype = p.planType;
  if (plantypeText[p.plantype]) adv.plantype = plantypeText[p.plantype];
  if (planstatusText[p.planstatus]) adv.planstatus = planstatusText[p.planstatus];
  if (p.ikrafttredelsesdato) adv.ikrafttredelsesdato = p.ikrafttredelsesdato;
  if (p.plannavn && p.kommune) adv.kommune = p.kommune;
  if (p.area) adv.areal = p.area;
  if (p.suitable && (p.formål || p.drawn)) { // show filtered/advanced properties:
    const codeYear = (p.planType === 'RbFormålOmråde') ? 1985 : 2008;
    if (!p.drawn) adv.plantype = p.planType + ' (PBL ' + codeYear + ')';
    if (p.feltnavn) adv.feltnavn = p.feltnavn;
    if (p.plankategori) adv.plankategori = p.plankategori.replace('de', 'de bruk');
    if (p.arealformål) adv.arealformål = description(p.arealformål);
    // Klimagassutslipp:
    const loggedIn = wizard.classList.contains('logged-in');
    const br = 'beregner...';
    const pk = 'pålogging kreves';
    const ph = loggedIn ? br : pk;
    adv['utslippseffekt på 5 år'] = `
      <span id="ut_${plan.getId()}">${p.utslipp || ph}</span>
    `;
    // From here on, we save properties (_...) which belongs to intersection:
    // arealkategori (from WMS)
    adv['_nåsituasjon'] = '<span class="_ns"></span>';
    // kat_nivå2 (from feature)
    adv['_planlagt situasjon'] = p.kat_nivå2 || 'Uendret';
    adv['_areal'] = `<span id="_a_${plan.getId()}"></span>`;
    const gk = getLayerById('grunnkart').getVisible();
    const gkp = 'grunnkartet må være på';
    const _ph = loggedIn ? (gk ? br : gkp) : pk;
    adv['_utslippseffekt på 5 år'] = `
      <span id="_ut_${plan.getId()}">${_ph}</span>
    `;
  }
  return adv;
};

const write = (tag, text, parent) => {
  const e = document.createElement(tag);
  e.innerHTML = text;
  parent.appendChild(e);
  return e;
};

const displayFeatureInfo = features => {
  const content = document.getElementById('popupContent');
  // write('h3', '#Objekt: ' + features.length, content);
  features.forEach((f, i) => {
    // write('b', 'Objekt ' + (i + 1) + ':', content);
    const adv = getAdvancedProperties(f);
    const plan = adv.plankategori;
    const t = plan ? 'Arealformål plan' : 'Planområde';
    const [blue, pink] = separateObjectKeysStartingWith(adv, '_');
    write('b', t + ':', content);
    const blueList = writeObject(blue, content);
    if (plan) blueList.classList.add('feature-emissions');
    // Single feature emissions:
    const fp = f.getProperties();
    if (!fp.utslipp && fp.suitable && (fp.formål || fp.drawn))
      sendFeaturesToBackend([f]).then(id => getEmissions(id).then(promised => {
        const [t5, t20, t75] = totalEmissionsFromTable(promised);
        const ut = document.getElementById('ut_' + f.getId());
        // null if you click on another feature meanwile
        if (ut) { ut.innerHTML = t5; f.set('utslipp', t5); }
    }).catch(e => console.log('Feil', e))).catch(e => console.log('Feil', e));

    const p = write('p', '', content);
    const hideP = write('a', '<u>Vis/Skjul originale egenskaper</u>', p);
    hideP.style.cursor = 'pointer';
    hideP.style.color = 'gray';
    const pList = writeObject(f.getProperties(), content);
    if (Object.keys(adv).length > 0) pList.hidden = true;
    hideP.onclick = () => pList.hidden = !pList.hidden;

    if (plan) {
      // Intersection of grunnkart with single feature:
      write('b', 'Grunnkart klippet til plankart:', content);
      const pinkList = writeObject(pink, content);
      pinkList.classList.add('intersection-emissions');
    }
    if (i + 1 < features.length) write('hr', '', content);
  });
};

const intersectionEmissions = (feature, wmsId, wkb, wkbLayer) => {
  // console.log(feature.get('planType'));
  if ((feature.get('arealformål') || feature.get('drawn')) && wkb) {
    const format = new WKB();
    const wkbFeature = format.readFeature(wkb.replace('\\x', ''), {
      featureProjection: map.getView().getProjection().getCode()
    });
    const wkbIntersection = intersection(feature, wkbFeature);
    if (wkbLayer) wkbLayer.getSource().addFeature(wkbIntersection);
    else map.addLayer(new VectorLayer({
      properties: { id: `wkb_${wmsId}`, type: 'wkb' },
      source: new VectorSource({ features: [ wkbIntersection ] }),
      style: highlightStyle('fuchsia', [10])
    }));
    wkbIntersection.set('title', 'klipp'); // avoid backend null error
    wkbIntersection.set('arealformål', feature.get('arealformål'));
    wkbIntersection.set('plankategori', feature.get('plankategori'));
    const fid = feature.getId();
    const area = document.getElementById('_a_' + fid);
    if (area) area.innerHTML = getAreaWithUnit(wkbIntersection);

    // Emissions of intersection between gk wkb and plan feature:
    sendFeaturesToBackend([wkbIntersection]).then(id =>
      getEmissions(id).then(promised => {
        const [t5, t20, t75] = totalEmissionsFromTable(promised);
        const ut_intersec = document.getElementById('_ut_' + fid);
        // null if you click on another feature meanwile
        if (ut_intersec) ut_intersec.innerHTML = t5;
      }).catch(e => console.log('Feil', e))
    ).catch(e => console.log('Feil', e));
  }
};

const displayVisibleWmsInfo = (wmses, coordinates, pixel, popup, f) => {
  wmses.forEach(wms => {
    const wmsId = wms.get('id');
    const wkbLayer = getLayerById(`wkb_${wmsId}`);
    if (wkbLayer) wkbLayer.getSource().clear();
    // console.log(wms.getData(pixel));
    if (wms.getVisible() && wms.getData(pixel)) {
      // console.log(wms.get('id'));
      getFeatureInfo(coordinates, map.getView(), wms).then(html => {
        if (html) {
          if (popupContent.childNodes.length) write('hr', '', popupContent);
          // popupContent.innerHTML += htmlString;
          // popupContent.appendChild(html.body);
          const table = html.body.querySelector('table');
          if (table) {
            popupContent.appendChild(table);
            showPopup(popup, coordinates);
            if (f) {
              const wkb = table.querySelector('#wkb')?.innerHTML;
              intersectionEmissions(f, wmsId, wkb, wkbLayer);
            }
          }
        }
      }).catch(err => console.log(err));;
    }
  });
};

export const createPopup = () => {
  const markerElement = document.getElementById('popupMarker');
  const x = document.getElementById('popupCloser');

  dragElement('popup', 'popupTopHandle');
  dragElement('popup', 'popupLeftHandle');

  const popup = new Overlay({
    autoPan: true, element: markerElement, offset: [-14, -30]
  });

  const hidePopup = () => {
    popup.setPosition(undefined);
    popupElement.hidden = true;
    removeListItemHighlight();
  };

  x.onclick = () => {
    hidePopup();
    x.blur();
    return false;
  };

  map.addOverlay(popup);

  map.on('pointermove', e => {
    const cursorOnFeature = map.hasFeatureAtPixel(e.pixel);
    map.getViewport().style.cursor = cursorOnFeature ? 'pointer': '';
  });

  const wmses = getWMSes(map);

  map.on('click', e => {
    if (!document.getElementById('selection').hidden) return;
    const popupContent = document.getElementById('popupContent');
    popupContent.innerHTML = '';
    const features = map.getFeaturesAtPixel( // files, drawings and WFSes
      e.pixel, { layerFilter: l => l.get('type') !== 'wkb' }
    ).filter(f => f.get('suitable'));// no internal prop, getFeaturesAtPixel bug
    let nextFeature;
    const h = getHighlightedFeature();
    removeHighlight();
    if (features.length) {
      // kat_nivå2 eller arealkategori per i dag (fra WMSen):
      const ak = 'Arealkategori';
      const dbk = 'Detaljert bebyggelsekategori';
      getSpecificFeatureInfo(
        [ak, dbk], 'grunnkart', e.coordinate, map.getView()
      ).then(info => {
        const e = document.getElementsByClassName('_ns');
        const l = e.length;
        // for (let i = 0; i < l; i++) writeObject(info, e[i]);
        for (let i = 0; i < l; i++) e[i].innerHTML = info[dbk] || info[ak] || '';
      }).catch(e => console.log(e));
      displayFeatureInfo(features);
      showPopup(popup, e.coordinate);

      let numberOfFeatures = 0;
      const hitFeatures = [];
      let relativePosition = -1;

      map.forEachFeatureAtPixel(e.pixel,
        (f, l) => { // To stop detection, return a truthy value.
          if (f.get('planid') && (f.get('arealformål') || f.get('drawn'))) {
            numberOfFeatures += 1;
            hitFeatures.push(f);
            if (h?.get('id') === f.get('id')) {
              relativePosition = numberOfFeatures - 1;
            }
          } else highlightCorrespondingListItem(f);
        },
        { layerFilter: l => ['drawings', 'file'].indexOf(l.get('type')) > -1 }
      );

      const hit = (numberOfFeatures > 0);
      if (hit) {
        const nextPosition = (relativePosition + 1) % numberOfFeatures;
        // console.log(nextPosition, numberOfFeatures);
        nextFeature = hitFeatures[nextPosition];
        if (nextFeature.get('arealformål') || nextFeature.get('drawn'))
          highlightFeature(nextFeature, 'blue');
        if (nextFeature.get('planid'))
          highlightCorrespondingListItem(nextFeature);
        // if (numberOfFeatures > 1)
        //   console.log(`Feature ${nextPosition + 1} of ${numberOfFeatures}`);
      }
    } else hidePopup();
    // They do not work well here because of asyncronous fetch:
    // if (popupContent.innerHTML === '') hidePopup();
    // else popup.setPosition(e.coordinate);

    displayVisibleWmsInfo(wmses, e.coordinate, e.pixel, popup, nextFeature);
  });
};

const plantypeText = {
  30: 'Eldre reguleringsplan',
  31: 'Mindre reguleringsendring',
  32: 'Bebyggelsesplan ihht. reguleringsplan',
  33: 'Bebyggelsesplan ihht. kommuneplan',
  34: 'Områderegulering',
  35: 'Detaljregulering'
};

const planstatusText = {
  1: 'Planlegging igangsatt',
  2: 'Planforslag',
  3: 'Endelig vedtatt arealplan ',
  4: 'Opphevet',
  5: 'Utgått/erstattet',
  6: 'Vedtatt plan med utsatt rettsvirkning',
  7: 'Endelig vedtatt plan underlagt tidsbegrensning',
  8: 'Overstyrt'
};
